import axios from 'axios';
import { supabase } from '../lib/supabase';

const IDME_CLIENT_ID = import.meta.env?.VITE_IDME_CLIENT_ID;
const IDME_CLIENT_SECRET = import.meta.env?.VITE_IDME_CLIENT_SECRET;
const IDME_REDIRECT_URI = import.meta.env?.VITE_IDME_REDIRECT_URI;
const IDME_API_BASE_URL = import.meta.env?.VITE_IDME_API_BASE_URL || 'https://api.id.me';
const IDME_SCOPE = import.meta.env?.VITE_IDME_SCOPE || 'military';

class IDmeService {
  /**
   * Generate ID.me OAuth authorization URL
   * @returns {string} Authorization URL
   */
  getAuthorizationUrl() {
    const params = new URLSearchParams({
      client_id: IDME_CLIENT_ID,
      redirect_uri: IDME_REDIRECT_URI,
      response_type: 'code',
      scope: IDME_SCOPE,
      state: this.generateState()
    });

    return `${IDME_API_BASE_URL}/oauth/authorize?${params?.toString()}`;
  }

  /**
   * Generate random state for CSRF protection
   * @returns {string} Random state string
   */
  generateState() {
    const state = Math.random()?.toString(36)?.substring(2, 15) + Math.random()?.toString(36)?.substring(2, 15);
    sessionStorage.setItem('idme_oauth_state', state);
    return state;
  }

  /**
   * Verify state parameter to prevent CSRF attacks
   * @param {string} state - State parameter from callback
   * @returns {boolean} True if state is valid
   */
  verifyState(state) {
    const savedState = sessionStorage.getItem('idme_oauth_state');
    sessionStorage.removeItem('idme_oauth_state');
    return state === savedState;
  }

  /**
   * Exchange authorization code for access token
   * @param {string} code - Authorization code from callback
   * @returns {Promise<Object>} Token response
   */
  async exchangeCodeForToken(code) {
    try {
      const response = await axios?.post(`${IDME_API_BASE_URL}/oauth/token`, {
        code,
        client_id: IDME_CLIENT_ID,
        client_secret: IDME_CLIENT_SECRET,
        redirect_uri: IDME_REDIRECT_URI,
        grant_type: 'authorization_code'
      });

      return response?.data;
    } catch (error) {
      console.error('ID.me token exchange error:', error);
      throw new Error('Failed to exchange authorization code for token');
    }
  }

  /**
   * Get user attributes from ID.me
   * @param {string} accessToken - Access token
   * @returns {Promise<Object>} User attributes
   */
  async getUserAttributes(accessToken) {
    try {
      const response = await axios?.get(`${IDME_API_BASE_URL}/api/public/v3/attributes.json`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response?.data;
    } catch (error) {
      console.error('ID.me get user attributes error:', error);
      throw new Error('Failed to retrieve user attributes');
    }
  }

  /**
   * Store ID.me verification status in Supabase
   * @param {string} userId - User ID
   * @param {Object} verificationData - Verification data from ID.me
   * @returns {Promise<Object>} Database response
   */
  async storeVerificationStatus(userId, verificationData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          idme_verified: true,
          idme_verification_date: new Date()?.toISOString(),
          idme_verification_data: verificationData
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Store verification status error:', error);
      throw new Error('Failed to store verification status');
    }
  }

  /**
   * Store ID.me credentials in database
   * @param {string} userId - User ID
   * @param {string} username - ID.me username
   * @param {string} password - ID.me password (will be encrypted)
   * @returns {Promise<Object>} Database response
   */
  async storeCredentials(userId, username, password) {
    try {
      console.log('Attempting to store ID.me credentials in auth.users for user:', userId);
      
      // Verify user is authenticated
      const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error('User not authenticated');
      }
      
      if (!authUser) {
        throw new Error('No authenticated user found');
      }
      
      console.log('Authenticated user ID:', authUser?.id);
      
      // Store credentials as Raw JSON object in auth.users.raw_user_meta_data
      const credentialsJson = {
        username: username,
        password: password, // Should be encrypted in production
        connected_at: new Date()?.toISOString()
      };
      
      // Get current user metadata
      const currentMetadata = authUser?.user_metadata || {};
      
      // Update user metadata with ID.me credentials
      const { data, error } = await supabase?.auth?.updateUser({
        data: {
          ...currentMetadata,
          idme_credentials: credentialsJson
        }
      });

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }
      
      console.log('ID.me credentials stored successfully in auth.users raw_user_meta_data:', data);
      return data;
    } catch (error) {
      console.error('Store ID.me credentials error:', error);
      throw new Error(`Failed to store ID.me credentials: ${error.message}`);
    }
  }

  /**
   * Check if user is verified with ID.me
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} True if verified
   */
  async isUserVerified(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('idme_verified, idme_verification_date')?.eq('id', userId)?.single();

      if (error) throw error;
      
      // Check if verification is still valid (e.g., within 90 days)
      if (data?.idme_verified && data?.idme_verification_date) {
        const verificationDate = new Date(data.idme_verification_date);
        const daysSinceVerification = (Date.now() - verificationDate?.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceVerification <= 90;
      }

      return false;
    } catch (error) {
      console.error('Check verification status error:', error);
      return false;
    }
  }

  /**
   * Initiate ID.me verification flow
   * Opens ID.me OAuth window
   */
  initiateVerification() {
    const authUrl = this.getAuthorizationUrl();
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback
   * @param {string} code - Authorization code
   * @param {string} state - State parameter
   * @param {string} userId - Current user ID
   * @returns {Promise<Object>} Verification result
   */
  async handleCallback(code, state, userId) {
    try {
      // Verify state to prevent CSRF
      if (!this.verifyState(state)) {
        throw new Error('Invalid state parameter');
      }

      // Exchange code for token
      const tokenData = await this.exchangeCodeForToken(code);
      
      // Get user attributes
      const userAttributes = await this.getUserAttributes(tokenData?.access_token);

      // Store verification status
      await this.storeVerificationStatus(userId, {
        verified_at: new Date()?.toISOString(),
        attributes: userAttributes
      });

      return {
        success: true,
        verified: true,
        attributes: userAttributes
      };
    } catch (error) {
      console.error('ID.me callback handling error:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }
}

export default new IDmeService();
